import { Button, ButtonProps, SvgIconProps } from '@mui/material';
import { PermissionWrappers, RequiredPermission } from 'shared/permissions';
import { ElementType, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps } from 'react-router-dom';

/**
 * Proprietà per il componente `EnhancedButton`.
 * Estende le proprietà standard del `Button` di Material UI, omettendo la prop `to` nativa
 * per evitare conflitti di tipo con React Router.
 */
export interface EnhancedButtonProps extends Omit<ButtonProps, 'to'> {
  /**
   * La parte finale o la chiave intera di traduzione i18next (es: "archive", "create_user").
   */
  label?: string;

  /**
   * Il prefisso opzionale per la traduzione. Se inserito, genera una chiave composta: `${prefix}.${label}`.
   */
  prefix?: string;

  /**
   * Rotta di destinazione per React Router. Se valorizzata, il bottone si trasforma
   * polimorficamente in un componente `Link`. Se omessa, rimane un tag `button` standard.
   */
  to?: LinkProps['to'];

  /**
   * Contatore numerico opzionale mostrato tra parentesi di fianco al testo (es: "In attesa (5)").
   */
  count?: number;

  /**
   * Flag di controllo per la visibilità condizionale. Se impostato a `false`, il componente restituisce immediatamente `null`.
   * @default true
   */
  visible?: boolean;

  /**
   * Componente SvgIcon di Material UI da visualizzare all'inizio del bottone (es: `AddIcon`, `DeleteIcon`).
   */
  icon?: ElementType<SvgIconProps>;

  /**
   * Proprietà native da passare direttamente al componente icona impostato (es: `{ color: 'primary', sx: { mr: 1 } }`).
   */
  iconProps?: SvgIconProps;

  /**
   * Callback di gestione del click nativa del bottone.
   */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;

  /**
   * Permesso singolo o array di permessi (RBAC) richiesti per renderizzare il componente.
   * I wrapper dei permessi vengono applicati in cascata (nidificati nell'ordine dell'array).
   *
   * @example requirePermission="superAdmin"
   * @example requirePermission={["requireAuth", "tenantAdministrator"]}
   */
  requirePermission?: RequiredPermission | RequiredPermission[];
}

/**
 * `EnhancedButton` è un componente core astratto ad alto livello (Polimorfico e Protetto).
 *
 * Svolge in automatico quattro compiti fondamentali:
 * 1. Gestisce la visibilità integrata (`visible`).
 * 2. Gestisce l'internazionalizzazione (`useTranslation`) includendo contatori dinamici opzionali (`count`).
 * 3. Commuta dinamicamente tra un bottone standard e un link di navigazione (`react-router-dom`) in base alla prop `to`.
 * 4. Protegge l'accesso avvolgendo l'interfaccia nei componenti Guard di sicurezza impostati (`requirePermission`).
 *
 * @component
 * @example
 * ```tsx
 * Bottone standard protetto da ruoli
 * <EnhancedButton
 *   label="save"
 *   variant="contained"
 *   requirePermission="tenantAdministrator"
 *   onClick={handleSave}
 * />
 *
 * Bottone Link di navigazione con contatore ed icona
 * <EnhancedButton
 *   label="technicians"
 *   prefix="dashboard"
 *   to="/tech/list"
 *   count={12}
 *   icon={PeopleIcon}
 * />
 * ```
 */
export function EnhancedButton({
  label,
  prefix = '',
  to,
  count,
  visible = true,
  icon: IconComponent,
  iconProps = {},
  variant = 'outlined',
  size = 'small',
  requirePermission,
  onClick,
  children, // Permette di fare override del testo se necessario
  ...props
}: EnhancedButtonProps) {
  const { t } = useTranslation();

  if (!visible) return null;

  // Gestione traduzione e contatore
  const translationKey = prefix ? `${prefix}.${label}` : label;
  const buttonText =
    children ?? (typeof count === 'number' ? `${t(translationKey)} (${count})` : t(translationKey));

  const renderIcon = IconComponent ? <IconComponent fontSize="small" {...iconProps} /> : undefined;

  let buttonElement = (
    <Button
      variant={variant}
      size={size}
      component={to ? Link : 'button'}
      startIcon={renderIcon}
      onClick={onClick}
      {...(to && { to })}
      {...props}>
      {buttonText}
    </Button>
  );

  // Applicazione dinamica dei wrapper dei permessi
  if (requirePermission) {
    const permissionsArray = Array.isArray(requirePermission)
      ? requirePermission
      : [requirePermission];
    permissionsArray.forEach((permKey) => {
      const WrapperComponent = PermissionWrappers[permKey];
      if (WrapperComponent) {
        buttonElement = <WrapperComponent>{buttonElement}</WrapperComponent>;
      }
    });
  }

  return buttonElement;
}
